import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { useState } from "react";
import { cx } from "../../../react-helpers/css";
import { Organization } from "../../../services/organization/organizationModel";
import {
  PREMIUM_SPONSORSHIP_STATUS_LABEL_KEYS,
  PremiumSponsorship,
} from "../../../services/premiumSponsorship/premiumSponsorshipModel";
import { premiumSponsorshipService } from "../../../services/premiumSponsorship/premiumSponsorshipService";
import { useConfirmationWithIntl } from "../../../components/ConfirmationDialog";
import useReload from "../../../hooks/useReload";
import ManagePremiumSponsorshipDialog from "./ManagePremiumSponsorshipDialog";
import SortableTh from "../../../components/SortableTh";
import { TableSort } from "../../../react-helpers/array";

const { deletePremiumSponsorship } = premiumSponsorshipService();

export type SponsorshipsSortKey = "status";

const PremiumSponsorshipsTable = ({
  organization,
  sponsorships,
  sort,
  onSort,
}: {
  organization: Organization;
  sponsorships: PremiumSponsorship[];
  sort: TableSort<SponsorshipsSortKey> | null;
  onSort: (key: SponsorshipsSortKey) => void;
}) => {
  const { t } = useTranslation(["organization"]);
  const { confirm } = useConfirmationWithIntl(["organization"]);
  const reload = useReload();
  const [managedSponsorship, setManagedSponsorship] =
    useState<PremiumSponsorship | null>(null);

  return (
    <>
      {sponsorships.length > 0 ? (
        <table className="lblock table">
          <thead>
            <tr>
              <th>
                {t("organization:premium-sponsorship.table-headers.EMAIL")}
              </th>
              <SortableTh sortKey="status" sort={sort} onSort={onSort}>
                {t("organization:premium-sponsorship.table-headers.STATUS")}
              </SortableTh>
              <th>
                {t("organization:premium-sponsorship.table-headers.SENT_DATE")}
              </th>
              <th>
                {t("organization:premium-sponsorship.table-headers.ACTION")}
              </th>
            </tr>
          </thead>
          <tbody>
            {sponsorships.map((sponsorship) => (
              <tr key={`premium-sponsorship-${sponsorship.id}`}>
                <td>{sponsorship.email}</td>
                <td>
                  <span
                    className={cx([
                      "user-status-chip --block",
                      sponsorship.status === "accepted" && "--active",
                      sponsorship.status === "declined" && "--declined",
                      sponsorship.status === "revoked" && "--revoked",
                    ])}
                  >
                    {t(
                      PREMIUM_SPONSORSHIP_STATUS_LABEL_KEYS[sponsorship.status],
                    )}
                  </span>
                </td>
                <td>{format(new Date(sponsorship.invitedAt), "dd/MM/yyyy")}</td>
                <td>
                  <div className="lrow --nowrap --center--v --gap--xs">
                    <button
                      className="btn--icon --simple --edit"
                      title={t("organization:premium-sponsorship.MANAGE_TIP")}
                      onClick={() => {
                        setManagedSponsorship(sponsorship);
                      }}
                    />
                    <button
                      className="btn--icon --simple --delete"
                      title={t("organization:premium-sponsorship.DELETE_TIP")}
                      onClick={() => {
                        confirm(
                          "organization:premium-sponsorship.confirm-delete.MESSAGE",
                          () =>
                            deletePremiumSponsorship(
                              organization.id,
                              sponsorship.id,
                            ).then(() => reload()),
                          true,
                          {
                            email: sponsorship.email,
                          },
                        );
                      }}
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <div className="info --compact lblock">
          {t("organization:premium-sponsorship.NO_SPONSORSHIPS_FOUND")}
        </div>
      )}

      {managedSponsorship && (
        <ManagePremiumSponsorshipDialog
          onClose={() => setManagedSponsorship(null)}
          organization={organization}
          sponsorship={managedSponsorship}
        />
      )}
    </>
  );
};

export default PremiumSponsorshipsTable;
